export interface FieldOption {
  value: string;
  label: string;
}

export type FieldType = 'text' | 'select' | 'chips' | 'multi' | 'textarea';

export interface FieldConfig {
  name: string;
  label: string;
  type: FieldType;
  hint?: string;
  placeholder?: string;
  options?: FieldOption[];
  maxLength?: number;
  rows?: number;
}

export interface SectionConfig {
  title: string;
  note?: string;
  fields: FieldConfig[];
}

export const stepTitles = [
  '基本信息',
  '检查报告',
  '病史与用药',
  '症状自查',
  '告诉我更多',
  '评估结果',
];

export const basicInfoSections: SectionConfig[] = [
  {
    title: '基本信息',
    fields: [
      {
        name: 'age',
        label: '年龄',
        type: 'text',
        placeholder: '例:47岁',
      },
      {
        name: 'body',
        label: '身高体重 / 体型自评',
        hint: '用于评估BMI和营养需求',
        type: 'text',
        placeholder: '例:163cm/58kg,或者偏胖/偏瘦',
      },
      {
        name: 'menstrual',
        label: '目前月经状态',
        type: 'select',
        options: [
          { value: 'regular', label: '规律来潮' },
          { value: 'irregular', label: '周期不规律,偶尔来潮' },
          { value: 'meno_under_1y', label: '已停经不到1年' },
          { value: 'meno_1_3y', label: '已停经1-3年' },
          { value: 'meno_over_3y', label: '已停经3年以上' },
          { value: 'surgical', label: '不确定/因手术停经' },
        ],
      },
      {
        name: 'last_period',
        label: '最近一次月经时间',
        type: 'text',
        placeholder: '例:2025年6月10日,或上个月',
      },
      {
        name: 'cycle_change',
        label: '近期月经周期变化',
        type: 'chips',
        options: [
          { value: 'shorter', label: '比以前短了' },
          { value: 'same', label: '和以前差不多' },
          { value: 'longer', label: '比以前长了' },
          { value: 'chaotic', label: '完全不规律' },
        ],
      },
      {
        name: 'flow_change',
        label: '近期经量变化',
        type: 'chips',
        options: [
          { value: 'less', label: '明显减少' },
          { value: 'same', label: '差不多' },
          { value: 'more', label: '明显增多' },
          { value: 'fluctuate', label: '忽多忽少' },
        ],
      },
      {
        name: 'period_color',
        label: '月经颜色/性状',
        type: 'chips',
        options: [
          { value: 'normal', label: '鲜红正常' },
          { value: 'dark', label: '颜色变深偏暗' },
          { value: 'pale', label: '颜色变淡偏少' },
          { value: 'clot', label: '有血块增多' },
        ],
      },
    ],
  },
];

export const reportSections: SectionConfig[] = [
  {
    title: '激素与血液检查',
    note: '有报告就照着填,没有就留空,不影响整体评估',
    fields: [
      {
        name: 'exam_date',
        label: '最近一次体检时间',
        type: 'text',
        placeholder: '例:2025年3月',
      },
      {
        name: 'fsh',
        label: '促卵泡激素 FSH',
        hint: '单位 mIU/mL',
        type: 'text',
        placeholder: '例:48(不知道可留空)',
      },
      {
        name: 'e2',
        label: '雌二醇 E2',
        hint: '单位 pg/mL',
        type: 'text',
        placeholder: '例:35(不知道可留空)',
      },
      {
        name: 'lipid',
        label: '血脂 / 血糖',
        type: 'chips',
        options: [
          { value: 'normal', label: '正常' },
          { value: 'high', label: '偏高' },
          { value: 'low', label: '偏低' },
          { value: 'never', label: '没查过' },
          { value: 'unknown', label: '不清楚' },
        ],
      },
    ],
  },
  {
    title: '影像与骨密度',
    fields: [
      {
        name: 'bone',
        label: '骨密度检查',
        type: 'chips',
        options: [
          { value: 'never', label: '没做过' },
          { value: 'normal', label: '正常' },
          { value: 'osteopenia', label: '骨量减少' },
          { value: 'osteoporosis', label: '骨质疏松' },
        ],
      },
      {
        name: 'breast',
        label: '乳腺检查',
        type: 'chips',
        options: [
          { value: 'never', label: '没做过' },
          { value: 'normal', label: '正常' },
          { value: 'nodule', label: '有结节' },
          { value: 'follow_up', label: '有异常需复查' },
        ],
      },
      {
        name: 'uterus',
        label: '妇科 B 超',
        type: 'chips',
        options: [
          { value: 'never', label: '没做过' },
          { value: 'normal', label: '正常' },
          { value: 'fibroid', label: '有肌瘤' },
          { value: 'cyst', label: '有囊肿' },
          { value: 'thick', label: '内膜增厚' },
        ],
      },
      {
        name: 'thyroid',
        label: '甲状腺功能',
        type: 'chips',
        options: [
          { value: 'never', label: '没查过' },
          { value: 'normal', label: '正常' },
          { value: 'abnormal', label: '有异常' },
          { value: 'unknown', label: '不清楚' },
        ],
      },
    ],
  },
];

export const historySections: SectionConfig[] = [
  {
    title: '病史与用药',
    fields: [
      {
        name: 'history',
        label: '既往病史',
        hint: '可多选',
        type: 'multi',
        options: [
          { value: 'none', label: '无' },
          { value: 'hypertension', label: '高血压' },
          { value: 'diabetes', label: '糖尿病' },
          { value: 'thyroid', label: '甲状腺疾病' },
          { value: 'breast', label: '乳腺疾病' },
          { value: 'fibroid', label: '子宫肌瘤' },
          { value: 'heart', label: '心脏病' },
          { value: 'osteoporosis', label: '骨质疏松' },
        ],
      },
      {
        name: 'surgery',
        label: '手术史',
        type: 'chips',
        options: [
          { value: 'none', label: '无' },
          { value: 'uterus', label: '子宫切除' },
          { value: 'ovary', label: '卵巢切除' },
          { value: 'other', label: '其他手术' },
        ],
      },
      {
        name: 'hrt',
        label: '激素补充治疗(HRT)',
        type: 'chips',
        options: [
          { value: 'never', label: '未使用' },
          { value: 'using', label: '正在使用' },
          { value: 'stopped', label: '曾经使用,现已停' },
        ],
      },
      {
        name: 'meds',
        label: '正在服用的药物或保健品',
        type: 'textarea',
        maxLength: 200,
        rows: 3,
        placeholder: '例:钙片、维生素D、降压药、中药...(没有可填"无")',
      },
      {
        name: 'allergy',
        label: '过敏史',
        type: 'text',
        placeholder: '例:青霉素过敏、海鲜过敏(没有可填"无")',
      },
      {
        name: 'family',
        label: '家族病史',
        hint: '可多选',
        type: 'multi',
        options: [
          { value: 'none', label: '无' },
          { value: 'breast_cancer', label: '乳腺癌' },
          { value: 'ovary_cancer', label: '卵巢癌' },
          { value: 'diabetes', label: '糖尿病' },
          { value: 'hypertension', label: '高血压' },
          { value: 'osteoporosis', label: '骨质疏松' },
        ],
      },
    ],
  },
];

export const symptomCategories = [
  {
    title: '血管舒缩与睡眠',
    items: [
      { name: 'sym_hot_flash', label: '潮热、阵阵发热' },
      { name: 'sym_night_sweat', label: '夜间盗汗、被子湿透' },
      { name: 'sym_insomnia', label: '入睡困难或半夜易醒' },
      { name: 'sym_palpitation', label: '心慌、心悸' },
    ],
  },
  {
    title: '情绪与精力',
    items: [
      { name: 'sym_mood', label: '情绪波动、容易发火' },
      { name: 'sym_anxiety', label: '焦虑、莫名紧张' },
      { name: 'sym_fatigue', label: '疲乏无力、提不起劲' },
      { name: 'sym_memory', label: '记忆力下降、注意力不集中' },
    ],
  },
  {
    title: '身体与私密处',
    items: [
      { name: 'sym_joint', label: '关节、肌肉酸痛' },
      { name: 'sym_dizzy', label: '头晕、头痛' },
      { name: 'sym_dryness', label: '阴道干涩、同房不适' },
      { name: 'sym_skin', label: '皮肤有蚂蚁爬行的感觉' },
    ],
  },
];

export const symptomLevels = [
  { value: 'none', label: '无', score: 0 },
  { value: 'mild', label: '轻', score: 1 },
  { value: 'moderate', label: '中', score: 2 },
  { value: 'severe', label: '重', score: 3 },
];

export const moreSections: SectionConfig[] = [
  {
    title: '告诉我更多',
    fields: [
      {
        name: 'top_symptoms',
        label: '最困扰你的 3 个症状',
        type: 'textarea',
        maxLength: 500,
        rows: 4,
        placeholder: '例:夜里老是热醒、白天心慌、情绪控制不住',
      },
      {
        name: 'needs',
        label: '你希望得到哪些帮助',
        hint: '可多选',
        type: 'multi',
        options: [
          { value: 'diet', label: '饮食营养' },
          { value: 'exercise', label: '运动方案' },
          { value: 'mood', label: '情绪调节' },
          { value: 'sleep', label: '睡眠改善' },
          { value: 'weight', label: '体重管理' },
          { value: 'doctor', label: '该不该去看医生' },
          { value: 'hrt', label: '激素治疗咨询' },
        ],
      },
      {
        name: 'concern',
        label: '还有什么想告诉郡主的',
        type: 'textarea',
        maxLength: 500,
        rows: 4,
        placeholder: '例:最近在吃中药、家里有老人要照顾、想明年去旅行...',
      },
      {
        name: 'contact',
        label: '联系方式',
        hint: '可留空',
        type: 'text',
        placeholder: '例:微信号 / 手机号,方便郡主回复你',
      },
    ],
  },
];